import { ContentContainerType } from 'types/enums';
import { ChakraEmptyList, ContentContainer } from 'app/components/Core';
import { Box, Checkbox, Text, useMultiStyleConfig } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { ChakraConfirmationDialog } from 'app/components/Dialogs';
import { VersionLyrics } from 'types/releases';
import sanitizeHtml from 'sanitize-html';
import { ReleaseActions } from '../ReleaseActions';

interface VersionLyricsComponentProps {
  onChangeExcluded: (isExcluded: boolean) => Promise<void>;
  isExcluded: boolean;
  updatingExclude: boolean;
  lyrics?: VersionLyrics;
  onSync: () => Promise<void>;
  isSyncing: boolean;
}

export const VersionLyricsComponent = ({
  onChangeExcluded,
  isExcluded,
  updatingExclude,
  lyrics,
  onSync,
  isSyncing,
}: VersionLyricsComponentProps) => {
  const styles = useMultiStyleConfig('TextEditor', {});
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  const renderOutput = () => {
    const body = lyrics?.body || '';
    switch (lyrics?.lyricVersion) {
      case 1: {
        return body;
      }
      case 2: {
        let __html = body.replaceAll('<p></p>', '<br>');
        __html = sanitizeHtml(__html, {
          transformTags: {
            h1: 'h4',
            p: 'div',
          },
        });
        return <Box dangerouslySetInnerHTML={{ __html }} />;
      }
      default: {
        return <></>;
      }
    }
  };

  const isEmpty = useMemo(() => {
    const virtualElement = document.createElement('div');
    virtualElement.innerHTML = lyrics?.body || '';
    return !virtualElement.textContent;
  }, [lyrics?.body]);

  return (
    <>
      <ContentContainer type={ContentContainerType.LIST} mb={24}>
        <ReleaseActions
          tab="lyrics"
          syncState={isSyncing}
          leftSocket={
            !isEmpty && (
              <Checkbox
                className="hasBorder"
                h={10}
                isChecked={isExcluded}
                isDisabled={updatingExclude}
                onChange={(e) => {
                  onChangeExcluded(e.target.checked);
                }}
              >
                Exclude from Release
              </Checkbox>
            )
          }
        />
        {isEmpty ? (
          <ChakraEmptyList
            title="No lyrics for this Song."
            primaryText="You can add lyrics in the Song and then Sync for this release and all information will be fetched."
          />
        ) : (
          <Box
            sx={{
              ...styles.root,
              px: 6,
              whiteSpace: 'pre-wrap',
              ...(isExcluded
                ? {
                    color: 'textSubdued',
                    opacity: 0.5,
                  }
                : {}),
              ...styles.text,
              ' *': {
                whiteSpace: 'pre-wrap',
              },
            }}
          >
            {renderOutput()}
          </Box>
        )}
      </ContentContainer>
      <ChakraConfirmationDialog
        title="Sync"
        actionButtonText="Sync"
        cancelButtonText="Cancel"
        isOpen={syncDialogOpen}
        handleAction={() => {
          setSyncDialogOpen(false);
          onSync();
        }}
        handleClose={() => setSyncDialogOpen(false)}
        onClose={() => setSyncDialogOpen(false)}
      >
        <Text fontSize="md">
          Syncing from the Song will fetch any updated lyrics.
        </Text>
      </ChakraConfirmationDialog>
    </>
  );
};
